import { cn } from '@/lib/utils'

interface SegmentedPickerProps<T extends string> {
  value: T
  onChange: (next: T) => void
  options?: readonly T[]
  label?: string
}

/**
 * The small segmented control these screens use for any short, exclusive choice:
 * the window (24h / 48h / 7d, or 7d / 30d / 90d) and the notification channel
 * (email / sms).
 *
 * A segmented control rather than a dropdown: there are two or three options,
 * they are all short, and the current one has to stay readable at a glance while
 * someone flicks back and forth comparing them.
 *
 * Nothing is ever disabled here. An option can legitimately come back empty — a
 * 7d window before the sync has a week behind it, a channel whose vendor sync is
 * down — and "no data yet" is something the reader should be able to see for
 * themselves rather than be locked out of.
 */
export function SegmentedPicker<T extends string>({
  value,
  onChange,
  options = ['24h', '48h', '7d'] as unknown as readonly T[],
  label = 'Window',
}: SegmentedPickerProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="border-line bg-input flex shrink-0 gap-1 rounded-lg border p-1"
    >
      {options.map((option) => (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={option === value}
          onClick={() => onChange(option)}
          className={cn(
            'rounded-md px-3 py-1.5 font-mono text-[11px] font-semibold tracking-[0.08em] uppercase',
            'transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
            option === value
              ? 'bg-surface-3 text-fg'
              : 'text-fg-muted hover:text-fg',
          )}
        >
          {option}
        </button>
      ))}
    </div>
  )
}
